/**
 * Korean strings for the keys the shell displays.
 *
 * Every user-visible string in the app is referenced by the same key the core
 * catalog uses, so replacing this map with the core translator is a one-file
 * change (§25). Keys, not sentences, are what the components pass around.
 */
export const MESSAGES: Record<string, string> = {
  /* §11 progressive status */
  'status.checkingFile': '파일을 확인하고 있습니다',
  'status.preparingPreview': '안전한 미리보기를 준비하고 있습니다',
  'status.firstPageReady': '첫 페이지가 준비되었습니다',
  'status.loadingRemaining': '나머지 내용을 불러오고 있습니다',
  'status.readyToEdit': '편집할 수 있습니다',
  'status.limitedSupport': '이 파일은 일부만 지원됩니다',
  'status.unvalidatedFormat': '이 형식은 아직 실제 파일로 검증되지 않았습니다 (텍스트만 추출)',
  'status.mayBeDamaged': '파일이 손상되었거나 완전하지 않을 수 있습니다',
  'status.originalUnchanged': '원본 파일은 변경되지 않았습니다',

  /* §34 errors */
  'error.unableToRead': '이 파일을 읽을 수 없습니다.',
  'error.damagedOrIncomplete': '파일이 손상되었거나 완전하지 않을 수 있습니다.',
  'error.passwordProtected': '이 파일은 암호로 보호되어 있습니다.',
  'error.featuresUnsupported': '이 파일의 일부 기능은 지원되지 않습니다.',
  'error.permissionExpired': '파일 접근 권한이 만료되었습니다.',
  'error.reopenFromSource': '원래 앱에서 파일을 다시 열어 주세요.',
  'error.notEnoughStorage': '저장 공간이 부족합니다.',
  'error.previewFailed': '미리보기를 만들지 못했습니다.',
  'error.editsSavedTemporarily': '편집 내용이 임시로 저장되었습니다.',
  'error.originalUnchanged': '원본 파일은 변경되지 않았습니다.',
  'error.mustSaveAsNewFile': '편집한 사본은 새 파일로 저장해야 합니다.',
  'error.formattingMayNotBePreserved': '일부 고급 서식은 유지되지 않을 수 있습니다.',
  'error.formulaNotRecalculated': '이 수식은 현재 다시 계산할 수 없습니다.',
  'error.conversionRequired': '이 문서는 편집하기 전에 변환이 필요합니다.',

  /* §30 detection */
  'detect.warning.unsupportedFormat': '아직 지원하지 않는 파일 형식입니다.',
  'detect.warning.macrosBlocked': '이 파일에는 매크로가 있습니다. 파일 안에 그대로 두되 실행하지 않습니다.',
  'detect.warning.sensitiveContent': '이 파일에는 인증 정보가 들어 있을 수 있습니다.',
  'detect.warning.extensionMismatch': '확장자와 실제 파일 내용이 일치하지 않습니다.',
  'detect.warning.mimeMismatch': '보낸 앱이 알려준 형식이 실제 내용과 다릅니다.',
  'detect.warning.encrypted': '이 파일은 암호화되어 있습니다.',
  'detect.warning.emptyFile': '이 파일은 비어 있습니다.',

  /* §30 security warnings surfaced while reading */
  'security.macrosPresent': '이 문서에는 매크로가 있습니다. 실행하지 않고 그대로 보존합니다.',
  'security.externalContent': '이 문서는 외부 콘텐츠를 참조합니다. 내려받지 않습니다.',
  'security.embeddedObject': '이 문서에는 임베드된 객체가 있습니다. 데이터를 해독하거나 실행하지 않습니다.',

  /* §21 archives */
  'archive.violation.unsafePath': '이 압축 파일에는 지정한 폴더 밖으로 풀리는 항목이 있습니다.',
  'archive.violation.tooManyEntries': '이 압축 파일에는 파일이 비정상적으로 많습니다.',
  'archive.violation.expansionTooLarge': '압축을 풀면 저장 공간을 과도하게 사용합니다.',
  'archive.masqueradeWarning': '문서처럼 보이지만 실제로는 실행 파일인 항목이 있습니다.',

  /* §31 file information */
  'fileInfo.fileName': '파일 이름',
  'fileInfo.fileType': '파일 형식',
  'fileInfo.detectedMime': '감지된 MIME',
  'fileInfo.fileSize': '파일 크기',
  'fileInfo.originalOrCopy': '원본 / 사본',
  'fileInfo.readOnly': '읽기 전용',
  'fileInfo.viewSupportLevel': '뷰잉 지원',
  'fileInfo.editSupportLevel': '편집 지원',
  'fileInfo.processedLocally': '기기에서 처리',
  'fileInfo.transmittedExternally': '외부 전송',
  'fileInfo.macrosBlocked': '매크로 차단',
  'fileInfo.accessSource': '열린 경로',
  'fileInfo.tempLocation': '임시 사본',
  'fileInfo.cacheDeletionTime': '임시본 삭제',
  'fileInfo.lastSaved': '마지막 저장',
  'fileInfo.saveResult': '저장 결과',
  'fileInfo.validationResult': '파일 검사',
  'fileInfo.passwordStatus': '암호 상태',
  'fileInfo.unsupportedSummary': '미지원 기능',
  'fileInfo.value.yes': '예',
  'fileInfo.value.no': '아니요',
  'fileInfo.value.never': '없음',
  'fileInfo.value.unknown': '알 수 없음',
  'fileInfo.value.original': '원본',
  'fileInfo.value.editedCopy': '편집 사본',

  /* §6 support levels */
  'support.full-view': '완전 뷰잉',
  'support.standard-view': '표준 뷰잉',
  'support.text-view': '텍스트 중심',
  'support.full-edit': '완전 편집',
  'support.basic-edit': '기본 편집',
  'support.annotate-edit': '주석 편집',
  'support.convert-edit': '변환 후 편집',
  'support.read-only': '읽기 전용',
  'support.needs-validation': '검증 중',
  'support.commercial-license': '상용 라이선스 필요',
  'support.unsupported': '미지원',
};

export function t(key: string): string {
  return MESSAGES[key] ?? key;
}
